import { css } from '@adornis/chemistry/directives/css.js';
/* eslint-disable complexity */
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { ParticipationEinzelberatung } from '@adornis/buchungsheld-db/entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '@adornis/buchungsheld-db/entities/ParticipationWorkshop.js';
import { type Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-search-selection.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import './components/bh-participation-modify-actions.js';
import './components/bh-participation-modify-new-user.js';

// TODO check redundancy with bh-prompt-user-picker

function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const productSelectionSet = {
  _class: 1,
  _id: 1,
  name: 1,
  slots: { _id: 1, _class: 1, isBooked: 1 },
} satisfies BaseQLSelectionSet<Einzelberatung | Workshop>;
type NarrowedProduct = NarrowedEntity<Einzelberatung | Workshop, typeof productSelectionSet>;

@customElement('bh-prompt-participation-modify')
export class BHPromptParticipationModify extends TrDialog<void> {
  static override get element_name() {
    return 'bh-prompt-participation-modify';
  }

  @property() institutionID = new RXController<Maybe<string>>(this, undefined);
  @property() workshopID = new RXController<Maybe<string>>(this, undefined);
  @property() productType = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly _product = new RXController(
    this,
    this.workshopID.observable.pipe(
      filter(Boolean),
      switchMap(id => getByID(Product, id)(productSelectionSet)),
      map(p => p as Maybe<NarrowedProduct>),
    ),
  );

  @state() protected readonly _users = new RXController(
    this,
    this.workshopID.observable.pipe(
      filter(Boolean),
      switchMap(id =>
        User.subscribeAllUsersWithoutParticipation(id)({
          email: 1,
          firstName: 1,
          lastName: 1,
          username: 1,
          roles: AdornisRoleToContextsWrapper.allFields,
        }),
      ),
    ),
  );

  @state() private readonly _participation = new RXController(
    this,
    combineLatest([
      this.workshopID.observable.pipe(filter(Boolean)),
      this.productType.observable.pipe(filter(Boolean)),
      this._product.observable.pipe(filter(Boolean)),
    ]).pipe(
      map(([id, type, product]) => {
        if (type === Workshop._class)
          return new ParticipationWorkshop({ productID: id, state: ParticipationState.WAITING });
        if (type === Einzelberatung._class) {
          const participation = new ParticipationEinzelberatung({ productID: id, state: ParticipationState.IN });
          const firstFreeSlot = product.slots.find(slot => !slot.isBooked);
          if (firstFreeSlot) participation.timeslotID = firstFreeSlot._id;
          return participation;
        }

        throw new Error('producttype not given!');
      }),
    ),
  );
  @state() protected _form = new FormController(this, this._participation.observable.pipe(filter(Boolean)), {});
  @state() protected _isEditMode = false;

  lastSearch = '';

  override render() {
    if (
      this._users.isLoading ||
      !this._users.value ||
      !this._form.document ||
      this._product.isLoading ||
      !this._product.value
    )
      return html` <x-loading-state no-wrapper></x-loading-state> `;

    return html`
      <x-flex space="md">
        ${this.additionalData(this._product.value)} ${this._isEditMode ? this.addNewParticipation() : this.chooseUser()}
      </x-flex>
    `;
  }

  protected additionalData(product: NarrowedProduct) {
    if (!(product instanceof Einzelberatung) || !(this._participation.value instanceof ParticipationEinzelberatung))
      return nothing;

    return html`
      <tr-grid space="xs" auto-fit-columns="100px">
        ${product.slots.map(timeslot => {
          const isActive = (this._participation.value as ParticipationEinzelberatung).timeslotID === timeslot._id;

          return html`
            <tr-flex
              @click=${() => {
                if (!this._participation.value || !(this._participation.value instanceof ParticipationEinzelberatung))
                  return;

                this._participation.value.timeslotID = timeslot._id;
                this.requestUpdate();
              }}
              space="sm"
              ${css({
                userSelect: 'none',
                background: isActive ? this.colors.accent : '#333',
                color: '#fff',
                padding: this.spacing.xs,
                borderRadius: this.sizes.borderRadius,
                cursor: 'pointer',
                ...(timeslot.isBooked ? { pointerEvents: 'none', opacity: '0.5' } : {}),
              })}
            >
              <tr-text> von ${timeslot.start.toLocaleString(DateTime.TIME_24_SIMPLE)} </tr-text>
              <tr-text> bis ${timeslot.end.toLocaleString(DateTime.TIME_24_SIMPLE)} </tr-text>
            </tr-flex>
          `;
        })}
      </tr-grid>
    `;
  }

  protected addNewParticipation() {
    if (!this._form.document?.user) throw new Error('user should be set by now');
    return html`
      <bh-participation-modify-new-user
        .institutionID=${this.institutionID.value}
        .user=${this._form.document.user}
        @back=${() => (this._isEditMode = false)}
        @cancel=${() => this.cancel()}
        @finish=${(e: ValueEvent<string>) => {
          if (!this._form.document) return;
          const userID = e.detail.value;
          console.log({ userID });
          this._form.document.user!._id = userID;
          this._form.document.userID = userID;
          this.addUserParticipation(this._form.document);
        }}
      ></bh-participation-modify-new-user>
    `;
  }

  protected chooseUser() {
    return html`
      <tr-dropdown-search-selection
        empty-state-with-search=${this._translation.translate('dropdowns', 'emptyStateUsers')}
        empty-state-without-search=${this._translation.translate('dropdowns', 'emptyStateUsersWithoutSearch')}
        placeholder="Nutzer auswählen"
        @value-picked=${(e: ValueEvent<User>) => {
          return this.switchToEditMode(e.detail.value);
        }}
        .renderString=${doc => (doc === null ? '+ Nutzer anlegen ' : doc?.email ?? '')}
        .search=${async search => {
          this.lastSearch = search ?? '';
          const users = this._users.value;
          if (!users || !search) return [];
          const emailRegexp = new RegExp(escapeRegExp(search), 'i');
          const matchingUsers = users.filter(
            user =>
              emailRegexp.test(user.email) ||
              emailRegexp.test(user.firstName) ||
              emailRegexp.test(user.lastName) ||
              emailRegexp.test(`${user.firstName} ${user.lastName}`) ||
              emailRegexp.test(`${user.lastName} ${user.firstName}`),
          );

          if (matchingUsers.length > 0) {
            const includeAddUserButton = !matchingUsers.find(user => user.email === search);
            return includeAddUserButton ? [...matchingUsers, null] : matchingUsers;
          }
          return [null];
        }}
      ></tr-dropdown-search-selection>
      ${this.buttons()}
    `;
  }

  protected buttons() {
    return html`
      <bh-participation-modify-actions
        @cancel=${() => this.cancel()}
        @save=${() => this._form.validate()}
      ></bh-participation-modify-actions>
    `;
  }

  protected async addUserParticipation(instance: ParticipationWorkshop | ParticipationEinzelberatung) {
    if (!instance.user) throw new Error('no user is set');
    console.log({ userID: instance.user._id });
    const action = Participation.addUserManuallyToWorkshop(
      instance.user._id,
      instance.productID,
      instance.state === ParticipationState.PARTICIPATED,
      instance instanceof ParticipationEinzelberatung ? instance.timeslotID.toString() : null,
    )();

    try {
      await XDialog.waitFor(action, this._translation.translate('saving') as string);
      return await this.close();
    } catch (e) {
      logger.error({ err: e }, 'Es gab ein Problem beim Anlegen eines neuen Nutzers in einer Veranstaltung');
      return XDialog.alert(`${this._translation.translate('PROMPT', 'user_couldnt_be_created_error')}`);
    }
  }

  static add(workshopID: string, productClass: string, institutionID: string) {
    return this.showPopup<Maybe<Appointment>>({
      props: { mode: 'add', workshopID, productType: productClass, institutionID },
    });
  }

  static edit(user: User) {
    return this.showPopup<Maybe<Appointment>>({ props: { mode: 'edit', admin: user } });
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  private async switchToEditMode(user: Maybe<User>) {
    if (!this._form.document) throw new Error('should not happen');
    const newUserID = A.getGloballyUniqueID();
    this._form.document.userID = user?._id ?? newUserID;
    const email = this.lastSearch;
    if (!user) this._form.document.user = new User({ _id: newUserID, email, username: email });
    else {
      const { stammdaten, ...rest } = User.allFields;
      const existingUser = await getByID(User, user._id)(rest);
      if (!existingUser) throw new Error('this user should exist already');
      this._form.document.user = existingUser;
    }
    this._isEditMode = true;
  }
}
